
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaChevronDown,
  FaChevronUp,
  FaCreditCard,
  FaGooglePay,
  FaUniversity,
  FaCheckCircle,
  FaPercentage,
  FaStickyNote,
  FaSuitcase,
  FaSnowflake,
  FaTicketAlt,
  FaExclamationTriangle,
  FaTag,
  FaCopy,
  FaMoneyBillWave,
} from "react-icons/fa";
import api from "../api/Api";

export default function BookingPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // =====================================================
  // STATE
  // =====================================================

  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPriceDetails, setShowPriceDetails] = useState(false);

  const [alertConfig, setAlertConfig] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    address: "",
    dropAddress: "",
    notes: "",
  });

  // partial | full | pay_later
  const [paymentMode, setPaymentMode] = useState("partial");

  const [gatewayMethod, setGatewayMethod] = useState("upi");

  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponMessage, setCouponMessage] = useState("");

  const [lastTapTime, setLastTapTime] = useState(0);

  // =====================================================
  // LOAD BOOKING DATA
  // =====================================================

  useEffect(() => {
    if (!location.state || !location.state.car) {
      navigate("/dashboard");
      return;
    }

    setBookingData(location.state);

    if (location.state.bookingType?.mode) {
      setPaymentMode(location.state.bookingType.mode);
    }

    const details = location.state.searchDetails;

    setFormData((prev) => ({
      ...prev,
      address: details?.pickupAddress || details?.pickCityName || "",
      dropAddress: details?.dropAddress || "",
    }));

    window.scrollTo(0, 0);
  }, [location, navigate]);

  // =====================================================
  // FETCH CUSTOMER PROFILE
  // =====================================================

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("customerToken");

        if (!token) return;

        const res = await api.get("/customer/profile", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const profile = res.data;

        setFormData((prev) => ({
          ...prev,
          fullName: profile.fullName || "",
          phone: profile.phone || "",
          email: profile.email || "",
        }));
      } catch (err) {
        console.error("Profile fetch failed:", err);
      }
    };

    fetchProfile();
  }, []);

  if (!bookingData) return null;

  const { car, searchDetails, bookingType } = bookingData;

  // =====================================================
  // PRICE CALCULATION
  // =====================================================

  const originalCost = bookingType?.originalCost || 0;

  // Existing automatic discount
  const autoDiscount = bookingType?.discountAmount || 0;

  // Base Fare after auto discount
  const baseFareAfterAutoDiscount = car?.basePrice || 0;

  // Coupon discount
  const afterPromoBaseFare = Math.max(
    0,
    baseFareAfterAutoDiscount - discount
  );

  const driverAllowance = car?.driverAllowance || 0;
  const nightCharge = car?.nightCharge || 0;
  const extraKmCharge = car?.extraKmCharge || 0;

  const subTotal =
    afterPromoBaseFare +
    extraKmCharge +
    driverAllowance +
    nightCharge;

  // GST
  const gstPercentage = car?.gst || 5;

  const dynamicGstAmount = Math.round(
    subTotal * (gstPercentage / 100)
  );

  // Total before full-payment discount
  const totalPriceWithGst = subTotal + dynamicGstAmount;

  // 3% discount ONLY for FULL PAYMENT
  const fullPayDiscount =
    paymentMode === "full"
      ? Math.round(totalPriceWithGst * 0.03)
      : 0;

  // Final total
  const finalTotalFare = Math.max(
    0,
    totalPriceWithGst - fullPayDiscount
  );

  // =====================================================
  // PAYMENT CALCULATION
  // =====================================================

  let amountToPayNow = 0;
  let amountToPayDriver = 0;

  if (paymentMode === "pay_later") {
    // -----------------------------------------
    // PAY LATER
    // -----------------------------------------
    amountToPayNow = 0;
    amountToPayDriver = finalTotalFare;
  } else if (paymentMode === "partial") {
    // -----------------------------------------
    // 20% ADVANCE
    // -----------------------------------------
    amountToPayNow = Math.round(finalTotalFare * 0.2);
    amountToPayDriver = finalTotalFare - amountToPayNow;
  } else if (paymentMode === "full") {
    // -----------------------------------------
    // FULL PAYMENT
    // -----------------------------------------
    amountToPayNow = finalTotalFare;
    amountToPayDriver = 0;
  }

  // =====================================================
  // ALERT
  // =====================================================

  const triggerAlert = (msg, type = "warning") => {
    setAlertConfig({
      show: true,
      message: msg,
      type,
    });
  };

  // =====================================================
  // APPLY COUPON
  // =====================================================

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      triggerAlert("Please enter coupon code.");
      return;
    }

    try {
      const payload = {
        couponCode: couponCode.toUpperCase(),
        cityId: searchDetails.pickCityId,
        serviceTypeId: searchDetails.serviceTypeId,
        travelDate: searchDetails.date,
        baseFare: car.basePrice || 0,
      };

      const response = await api.post(
        "/carRoutes/validate-promo",
        payload
      );

      if (response.data.success) {
        const calculatedDiscount =
          response.data.discountAmount || 0;

        // For partial payment check advance amount
        if (paymentMode === "partial") {
          const advanceAfterAuto = Math.round(
            (originalCost - autoDiscount) * 0.2
          );

          if (advanceAfterAuto < calculatedDiscount) {
            const msg = `Advance amount is less than ₹${calculatedDiscount}. Switch to Full Payment to use this coupon.`;

            setCouponMessage({
              type: "error",
              text: msg,
            });

            triggerAlert(msg, "warning");
            return;
          }
        }

        setDiscount(calculatedDiscount);

        setCouponMessage({
          type: "success",
          text: response.data.message || "Coupon Applied Successfully",
        });
      }
    } catch (error) {
      setDiscount(0);

      const errorMsg =
        error?.response?.data?.message ||
        "Invalid Coupon Code";

      setCouponMessage({
        type: "error",
        text: errorMsg,
      });
    }
  };

  // =====================================================
  // MOBILE DOUBLE TAP COUPON
  // =====================================================

  const handleMobileDoubleTap = () => {
    const now = Date.now();

    const timeSinceLastTap = now - lastTapTime;

    if (
      timeSinceLastTap < 350 &&
      timeSinceLastTap > 0
    ) {
      const codeToApply = "PATRA500";

      setCouponCode(codeToApply);

      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(codeToApply)
          .catch(() => {});
      }

      setTimeout(() => {
        const button = document.getElementById(
          "apply-coupon-btn"
        );

        if (button) {
          button.click();
        }
      }, 100);
    }

    setLastTapTime(now);
  };

  // =====================================================
  // HANDLE PAYMENT / BOOKING
  // =====================================================

  const handlePayment = async () => {
    const { activeTab } = searchDetails;

    const needsDropAddress =
      activeTab === "oneway" ||
      activeTab === "round";

    // -----------------------------------------------------
    // VALIDATION
    // -----------------------------------------------------

    if (
      !formData.fullName ||
      !formData.phone ||
      !formData.address
    ) {
      triggerAlert(
        "Please fill in Name, Phone, and Pickup Address."
      );
      return;
    }

    if (!/^\d{10}$/.test(formData.phone)) {
      triggerAlert(
        "Please enter a valid 10-digit phone number"
      );
      return;
    }

    if (
      needsDropAddress &&
      !formData.dropAddress
    ) {
      triggerAlert(
        "Please provide a Drop Address for this trip."
      );
      return;
    }

    setLoading(true);

    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("customerToken");

      // ===================================================
      // DURATION DATA
      // ===================================================

      const durationName = (() => {
        const {
          activeTab,
          durationName,
          durationId,
          durationHours,
          taxipkg,
          taxiId,
        } = searchDetails;

        if (activeTab === "local") {
          return {
            type: "local",
            label: durationName,
            id: durationId,
            hours: durationHours,
          };
        }

        if (activeTab === "taxi") {
          return {
            type: "taxi",
            package: taxipkg,
            id: taxiId,
          };
        }

        if (activeTab === "round") {
          return {
            type: "round",
            label: "Round Trip",
            days: searchDetails.totalDays || 1,
          };
        }

        if (activeTab === "oneway") {
          return {
            type: "oneway",
            label: "One Way",
          };
        }

        return {
          type: activeTab,
        };
      })();

      // ===================================================
      // PAYMENT TYPE
      // ===================================================

      /*
        paymentType:
        1 = Full Payment
        2 = Partial / 20% Advance
        3 = Pay Later

        IMPORTANT:
        Agar backend me Pay Later ke liye koi aur value
        defined hai, to yahan 3 ko us value se replace karein.
      */

      let paymentType = 2;
      let advPercent = 20;
      let payableAmount = amountToPayNow;

      if (paymentMode === "full") {
        paymentType = 1;
        advPercent = 100;
        payableAmount = amountToPayNow;
      }

      if (paymentMode === "partial") {
        paymentType = 2;
        advPercent = 20;
        payableAmount = amountToPayNow;
      }

      if (paymentMode === "pay_later") {
        paymentType = 3;
        advPercent = 0;
        payableAmount = 0;
      }

      // ===================================================
      // PAYLOAD
      // ===================================================

      const payload = {
        bookingData: {
          pickCityId: searchDetails.pickCityId,
          serviceTypeId: searchDetails.serviceTypeId,

          date: searchDetails.date,
          time: searchDetails.timeId,

          returnDate:
            searchDetails.returnDate || null,

          returnTimeId:
            searchDetails.returnTimeId || null,

          fullName: formData.fullName,
          mobile: formData.phone,
          email: formData.email,

          pickupAddress: formData.address,
          dropAddress: formData.dropAddress,

          notes: formData.notes,

          appliedCoupon:
            discount > 0 ? couponCode : null,

          couponDiscountAmt: discount,
        },

        durationName,

        cab: {
          carId: car.carId || car.id,
          carName: car.carName || car.name,

          baseFare: car.basePrice,

          finalPrice: finalTotalFare,

          tariffId: car.tariffId || null,

          nightCharge: car.nightCharge || 0,

          totalDistance: car.kmIncluded || 0,

          driverAllowance:
            car.driverAllowance || 0,

          totalSavings:
            autoDiscount +
            fullPayDiscount +
            discount,

          gstPercent: car.gst,

          gstAmount: dynamicGstAmount,
        },

        // -----------------------------------------------
        // PAYMENT INFORMATION
        // -----------------------------------------------

        paymentType,

        payableAmount,

        advPercent,

        // Extra clear information for backend
        paymentMode,

        paymentStatus:
          paymentMode === "pay_later"
            ? "pending"
            : "initiated",
      };

      // console.log(
      //   "BOOKING PAYLOAD:",
      //   JSON.stringify(payload, null, 2)
      // );

      const config = token
        ? {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        : {};

      // ===================================================
      // CREATE BOOKING
      // ===================================================

      const response = await api.post(
        "/bookingRoutes/create",
        payload,
        config
      );

      // ===================================================
      // SUCCESS
      // ===================================================

      if (response?.data?.success) {
        // =================================================
        // PAY LATER
        // =================================================

        if (paymentMode === "pay_later") {
          navigate("/booking-success", {
            state: {
              car,
              searchDetails,

              customer: formData,

              tripDetails:
                response.data.trip_info,

              paymentInfo: {
                totalFare: finalTotalFare,

                paidAmount: 0,

                balanceAmount: finalTotalFare,

                paymentMode: "Pay Later",
              },

              bookingId:
                response.data.enqref_no,

              confirmationId:
                response.data.confirmation_id,

              txnid:
                response.data.txnid,

              paymentStatus: "Pending",
            },
          });

          return;
        }

        // =================================================
        // ONLINE PAYMENT
        // =================================================

        if (
          amountToPayNow > 0 &&
          response.data.payuUrl &&
          response.data.paymentData
        ) {
          const {
            payuUrl,
            paymentData,
          } = response.data;

          const form =
            document.createElement("form");

          form.method = "POST";
          form.action = payuUrl;

          for (const key in paymentData) {
            const input =
              document.createElement("input");

            input.type = "hidden";
            input.name = key;
            input.value = paymentData[key];

            form.appendChild(input);
          }

          document.body.appendChild(form);

          form.submit();

          return;
        }

        // =================================================
        // FALLBACK SUCCESS
        // =================================================

        navigate("/booking-success", {
          state: {
            car,
            searchDetails,

            customer: formData,

            tripDetails:
              response.data.trip_info,

            paymentInfo: {
              totalFare: finalTotalFare,

              paidAmount: amountToPayNow,

              balanceAmount: amountToPayDriver,

              paymentMode:
                paymentMode === "partial"
                  ? "Partial (20%)"
                  : "Full Payment",
            },

            bookingId:
              response.data.enqref_no,

            confirmationId:
              response.data.confirmation_id,

            txnid:
              response.data.txnid,
          },
        });
      } else {
        triggerAlert(
          response?.data?.message ||
            "Booking could not be created."
        );
      }
    } catch (error) {
      console.error(
        "BOOKING ERROR:",
        error
      );

      const errorMessage =
        error?.response?.data?.message ||
        "Something went wrong while confirming your ride. Please try again.";

      triggerAlert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // RETURN JSX
  // =====================================================

  return (
    <div className="d-flex flex-column min-vh-100 bg-white font-sans">

      {/* =================================================
          ALERT
      ================================================= */}

      {alertConfig.show && (
        <div className="android-alert-overlay">
          <div className="android-alert-card animate-pop-in">

            <div className="p-4">

              <h5
                className={`fw-bold mb-3 d-flex align-items-center gap-2 ${
                  alertConfig.type === "success"
                    ? "text-success"
                    : "text-dark"
                }`}
              >
                {alertConfig.type === "success" ? (
                  <FaCheckCircle className="text-success" />
                ) : (
                  <FaExclamationTriangle className="text-warning" />
                )}

                {alertConfig.type === "success"
                  ? "Success"
                  : "Alert"}
              </h5>

              <p className="text-muted mb-4">
                {alertConfig.message}
              </p>

              <div className="text-end">

                <button
                  className={`btn fw-bold ${
                    alertConfig.type === "success"
                      ? "text-success"
                      : "text-primary"
                  }`}
                  onClick={() =>
                    setAlertConfig({
                      show: false,
                      message: "",
                      type: "warning",
                    })
                  }
                >
                  OK
                </button>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* =================================================
          MAIN CONTAINER
      ================================================= */}

      <div
        className="container-fluid px-3 pt-3 flex-grow-1"
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          paddingBottom: "100px",
        }}
      >

        {/* =================================================
            TRIP SUMMARY
        ================================================= */}

        <div className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden">

          <div className="card-body p-3">

            <div className="d-flex align-items-center mb-3">

              <div
                className="bg-light rounded-3 p-2 me-3 border d-flex align-items-center justify-content-center"
                style={{
                  width: "85px",
                  height: "65px",
                }}
              >
                <img
                  src={car.image}
                  alt={car.name}
                  className="img-fluid"
                  style={{
                    objectFit: "contain",
                  }}
                />
              </div>

              <div className="flex-grow-1">

                <h6 className="fw-bold m-0 text-capitalize text-dark">
                  {car.name}
                </h6>

                <div className="d-flex gap-1 mt-1 flex-wrap">

                  <span
                    className="badge bg-warning text-dark px-2 py-1"
                    style={{
                      fontSize: "0.65rem",
                    }}
                  >
                    {searchDetails.activeTab ===
                    "round"
                      ? "Round Trip"
                      : searchDetails.activeTab}
                  </span>

                  {searchDetails.activeTab ===
                    "local" &&
                    searchDetails.durationName && (
                      <span
                        className="badge bg-primary text-white px-2 py-1"
                        style={{
                          fontSize: "0.65rem",
                        }}
                      >
                        {searchDetails.durationName}
                      </span>
                    )}

                  {searchDetails.activeTab ===
                    "taxi" &&
                    searchDetails.taxipkg && (
                      <span
                        className="badge bg-success text-white px-2 py-1"
                        style={{
                          fontSize: "0.65rem",
                        }}
                      >
                        {searchDetails.taxipkg}
                      </span>
                    )}

                </div>
              </div>
            </div>

            {/* CAR FEATURES */}

            <div className="d-flex justify-content-between align-items-center bg-light rounded px-3 py-2 mb-3 border small text-dark">

              <span className="fw-semibold">
                <FaUser className="me-1 text-muted" />
                {car.seats} Seats
              </span>

              <span className="fw-semibold">
                <FaSuitcase className="me-1 text-muted" />
                {car.luggage || 2} Bags
              </span>

              <span className="fw-semibold">
                <FaSnowflake className="text-info me-1" />
                {car.ac === "Yes"
                  ? "AC"
                  : "Non-AC"}
              </span>

            </div>

            {/* ROUTE */}

            <div className="d-flex align-items-start gap-2 mb-2">

              <div className="d-flex flex-column align-items-center mt-1">

                <div
                  className="bg-success rounded-circle"
                  style={{
                    width: "8px",
                    height: "8px",
                  }}
                />

                {(searchDetails.activeTab ===
                  "oneway" ||
                  searchDetails.activeTab ===
                    "round") && (
                  <>
                    <div
                      className="vr"
                      style={{
                        height: "20px",
                        opacity: 0.3,
                      }}
                    />

                    <div
                      className="bg-danger rounded-circle"
                      style={{
                        width: "8px",
                        height: "8px",
                      }}
                    />
                  </>
                )}

              </div>

              <div className="flex-grow-1 small">

                <div className="fw-bold text-dark">
                  {searchDetails.pickupAddress ||
                    searchDetails.pickCityName}
                </div>

                {(searchDetails.activeTab ===
                  "oneway" ||
                  searchDetails.activeTab ===
                    "round") && (
                  <div className="fw-bold mt-2 text-muted">
                    {searchDetails.dropAddress}
                  </div>
                )}

              </div>

              <div className="text-end small text-muted">

                <div className="fw-semibold">
                  {new Date(
                    searchDetails.date
                  ).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>

                <div>
                  {searchDetails.timeName}
                </div>

                {searchDetails.activeTab ===
                  "round" &&
                  searchDetails.returnDate && (
                    <div className="mt-2">

                      <div className="fw-semibold text-danger">
                        Ret:{" "}
                        {new Date(
                          searchDetails.returnDate
                        ).toLocaleDateString(
                          "en-GB",
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </div>

                      <div className="text-danger">
                        {searchDetails.returnTimeName ||
                          "Selected Time"}
                      </div>

                    </div>
                  )}

              </div>
            </div>

          </div>

          {/* TOTAL FARE */}

          <div
            className="bg-light border-top p-3 d-flex justify-content-between align-items-center cursor-pointer"
            onClick={() =>
              setShowPriceDetails(
                !showPriceDetails
              )
            }
          >
            <span className="small fw-bold text-secondary">
              Total Fare
            </span>

            <div className="d-flex align-items-center gap-2">

              <span className="fw-bold text-dark">
                ₹{finalTotalFare.toLocaleString()}
              </span>

              {showPriceDetails ? (
                <FaChevronUp
                  size={12}
                  className="text-muted"
                />
              ) : (
                <FaChevronDown
                  size={12}
                  className="text-muted"
                />
              )}

            </div>
          </div>

          {/* PRICE DETAILS */}

          {showPriceDetails && (
            <div className="p-3 border-top small animate-slide-down bg-white">

              <div className="d-flex justify-content-between mb-1 text-muted">
                <span>Rental Price</span>
                <span>
                  ₹{(car.basePrice || 0).toLocaleString()}
                </span>
              </div>

              {car.driverAllowance > 0 && (
                <div className="d-flex justify-content-between mb-1 text-muted">
                  <span>Driver Allowance</span>
                  <span>
                    ₹{(
                      car.driverAllowance || 0
                    ).toLocaleString()}
                  </span>
                </div>
              )}

              {car.gst > 0 && (
                <div className="d-flex justify-content-between mb-1 text-muted">
                  <span>
                    GST ({car.gst || 0}%)
                  </span>

                  <span>
                    ₹{dynamicGstAmount.toLocaleString()}
                  </span>
                </div>
              )}

              {car.nightCharge > 0 && (
                <div className="d-flex justify-content-between mb-1 text-muted">
                  <span>Night Charge</span>

                  <span>
                    ₹{(
                      car.nightCharge || 0
                    ).toLocaleString()}
                  </span>
                </div>
              )}

              <div className="d-flex justify-content-between mb-1 text-success fw-bold">

                <span>
                  <FaTag className="me-1" />
                  Special Discount (1%)
                </span>

                <span>
                  -₹{autoDiscount.toLocaleString()}
                </span>

              </div>

              {fullPayDiscount > 0 && (
                <div className="d-flex justify-content-between mb-1 text-success fw-bold">

                  <span>
                    <FaTag className="me-1" />
                    Full Pay Discount (3%)
                  </span>

                  <span>
                    -₹{fullPayDiscount.toLocaleString()}
                  </span>

                </div>
              )}

              {discount > 0 && (
                <div className="d-flex justify-content-between mb-1 text-success fw-bold">

                  <span>
                    <FaTicketAlt className="me-1" />
                    Coupon Discount
                  </span>

                  <span>
                    -₹{discount.toLocaleString()}
                  </span>

                </div>
              )}

              <div
                className="mt-2 text-danger"
                style={{
                  fontSize: "0.75rem",
                }}
              >
                * Toll, State Tax & Parking charges
                are extra as applicable.
              </div>

              <div className="border-top my-2" />

              <div className="d-flex justify-content-between fw-bold fs-6 text-dark">

                <span>Final Total</span>

                <span className="text-success">
                  ₹{finalTotalFare.toLocaleString()}
                </span>

              </div>

            </div>
          )}

        </div>

        {/* =================================================
            COUPON
        ================================================= */}

        <div className="card border-0 shadow-sm rounded-4 mb-3">

          <div className="card-body p-3">

            <label className="fw-bold text-secondary small mb-2 d-flex align-items-center gap-1">
              <FaTicketAlt className="text-warning" />
              APPLY COUPON
            </label>

            {!discount && (
              <div
                className="bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-3 p-2 mb-3 text-center cursor-pointer transition-all hover-shadow"
                onClick={handleMobileDoubleTap}
                style={{
                  userSelect: "none",
                  touchAction: "manipulation",
                }}
              >

                <small className="text-primary fw-bold d-flex align-items-center justify-content-center gap-2">

                  <FaCopy />

                  Apply this Coupon:

                  <span className="fs-6 bg-white px-2 py-1 rounded border border-primary border-opacity-50 text-dark">
                    PATRA500
                  </span>

                </small>

                <div
                  style={{
                    fontSize: "0.65rem",
                  }}
                  className="text-muted mt-1 fw-semibold"
                >
                  Save Up to ₹500 on trips above ₹1000
                </div>

              </div>
            )}

            <div className="input-group">

              <input
                type="text"
                className="form-control bg-light border-0 fw-bold text-uppercase"
                placeholder="Enter Code"
                value={couponCode}
                onChange={(e) =>
                  setCouponCode(
                    e.target.value.toUpperCase()
                  )
                }
                disabled={discount > 0}
              />

              {discount > 0 ? (
                <button
                  type="button"
                  className="btn btn-danger px-4 fw-bold"
                  onClick={() => {
                    setDiscount(0);
                    setCouponCode("");
                    setCouponMessage("");
                  }}
                >
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  id="apply-coupon-btn"
                  className="btn btn-dark px-4 fw-bold"
                  onClick={handleApplyCoupon}
                >
                  Apply
                </button>
              )}

            </div>

            {couponMessage && (
              <small
                className={`d-block mt-2 fw-semibold ${
                  couponMessage.type ===
                  "success"
                    ? "text-success"
                    : "text-danger"
                }`}
              >
                {couponMessage.text}
              </small>
            )}

          </div>
        </div>

        {/* =================================================
            CUSTOMER DETAILS
        ================================================= */}

        <h6 className="ms-1 mt-4 mb-2 small fw-bold text-secondary text-uppercase">
          Customer Details
        </h6>

        <div className="card border border-secondary border-opacity-25 shadow-sm rounded-4 mb-3">

          <div className="card-body p-3">

            {/* NAME */}

            <div className="mb-3 position-relative">

              <FaUser
                className="position-absolute text-muted"
                style={{
                  top: "12px",
                  left: "12px",
                }}
              />

              <input
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                placeholder="Full Name"
                value={formData.fullName}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    fullName: e.target.value,
                  })
                }
                readOnly
              />

            </div>

            {/* PHONE */}

            <div className="mb-3 position-relative">

              <FaPhoneAlt
                className="position-absolute text-muted"
                style={{
                  top: "12px",
                  left: "12px",
                }}
              />

              <input
                type="tel"
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                placeholder="Phone Number"
                maxLength={10}
                value={formData.phone}
                onChange={(e) => {
                  const onlyNums =
                    e.target.value.replace(
                      /\D/g,
                      ""
                    );

                  setFormData({
                    ...formData,
                    phone: onlyNums,
                  });
                }}
                readOnly
              />

            </div>

            {/* EMAIL */}

            <div className="mb-3 position-relative">

              <FaEnvelope
                className="position-absolute text-muted"
                style={{
                  top: "12px",
                  left: "12px",
                }}
              />

              <input
                type="email"
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    email: e.target.value,
                  })
                }
                readOnly
              />

            </div>

            {/* PICKUP */}

            <div className="mb-3 position-relative">

              <FaMapMarkerAlt
                className="position-absolute text-success"
                style={{
                  top: "12px",
                  left: "12px",
                }}
              />

              <textarea
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                rows="2"
                placeholder="Pickup Address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: e.target.value,
                  })
                }
              />

            </div>

            {/* DROP */}

            <div className="mb-3 position-relative">

              <FaMapMarkerAlt
                className="position-absolute text-danger"
                style={{
                  top: "12px",
                  left: "12px",
                }}
              />

              <textarea
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                rows="2"
                placeholder="Drop Address"
                value={formData.dropAddress}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dropAddress: e.target.value,
                  })
                }
              />

            </div>

            {/* NOTES */}

            <div className="position-relative mb-0">

              <FaStickyNote
                className="position-absolute text-muted"
                style={{
                  top: "12px",
                  left: "12px",
                }}
              />

              <input
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                placeholder="Any instructions for driver?"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    notes: e.target.value,
                  })
                }
              />

            </div>

          </div>
        </div>

        {/* =================================================
            PAYMENT OPTION
        ================================================= */}

        <h6 className="ms-1 mt-4 mb-2 small fw-bold text-secondary text-uppercase">
          Payment Option
        </h6>

        <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">

          {/* PAYMENT TABS */}

          <div className="d-flex p-1 bg-light m-2 rounded-3 gap-1">

            {/* PARTIAL */}

            <button
              type="button"
              className={`flex-fill btn btn-sm rounded-2 py-2 px-3 fw-bold transition-all ${
                paymentMode === "partial"
                  ? "bg-white shadow-sm text-warning"
                  : "text-muted border-0"
              }`}
              onClick={() =>
                setPaymentMode("partial")
              }
            >
              20% Advance
            </button>

            {/* FULL */}

            <button
              type="button"
              className={`flex-fill btn btn-sm rounded-2 py-2 px-3 fw-bold transition-all ${
                paymentMode === "full"
                  ? "bg-white shadow-sm text-primary"
                  : "text-muted border-0"
              }`}
              onClick={() =>
                setPaymentMode("full")
              }
            >
              Full Pay{" "}
              <span className="badge bg-success ms-1">
                3% OFF
              </span>
            </button>

            {/* PAY LATER */}

            <button
              type="button"
              className={`flex-fill btn btn-sm rounded-2 py-2 px-3 fw-bold transition-all ${
                paymentMode === "pay_later"
                  ? "bg-white shadow-sm text-danger"
                  : "text-muted border-0"
              }`}
              onClick={() =>
                setPaymentMode("pay_later")
              }
            >
              Pay Later
            </button>

          </div>

          {/* =================================================
              PAYMENT CONTENT
          ================================================= */}

          <div className="card-body p-0">

            <div className="p-3 animate-fade-in">

              {/* =============================================
                  PAY LATER
              ============================================= */}

              {paymentMode === "pay_later" && (
                <div
                  className="p-3 rounded-3 mb-3 border"
                  style={{
                    backgroundColor: "#fff3cd",
                    borderColor: "#ffc107",
                  }}
                >

                  <div className="d-flex align-items-center mb-1">

                    <FaMoneyBillWave
                      size={14}
                      className="me-2 text-warning"
                    />

                    <span className="fw-bold small text-dark">
                      Pay Later — No Payment Now
                    </span>

                  </div>

                  <small className="text-muted d-block">
                    Your booking will be confirmed
                    without any online payment.
                    Total fare{" "}
                    <b>
                      ₹{finalTotalFare.toLocaleString()}
                    </b>{" "}
                    will be payable later.
                  </small>

                </div>
              )}

              {/* =============================================
                  PARTIAL
              ============================================= */}

              {paymentMode === "partial" && (
                <div className="p-3 bg-warning bg-opacity-10 rounded-3 mb-3 border border-warning border-opacity-25">

                  <div className="d-flex align-items-center mb-1">

                    <FaPercentage
                      size={14}
                      className="me-2 text-warning"
                    />

                    <span className="fw-bold small text-dark">
                      Secure Booking with 20% Advance
                    </span>

                  </div>

                  <small className="text-muted d-block">

                    Pay{" "}
                    <b>
                      ₹{amountToPayNow.toLocaleString()}
                    </b>{" "}
                    now to confirm your ride.

                    Remaining{" "}
                    <b>
                      ₹{amountToPayDriver.toLocaleString()}
                    </b>{" "}
                    pay to driver.

                  </small>

                </div>
              )}

              {/* =============================================
                  FULL
              ============================================= */}

              {paymentMode === "full" && (
                <div className="p-3 bg-success bg-opacity-10 rounded-3 mb-3 border border-success border-opacity-25">

                  <div className="d-flex align-items-center mb-1">

                    <FaCheckCircle
                      size={14}
                      className="me-2 text-success"
                    />

                    <span className="fw-bold small text-dark">
                      Full Payment — 3% Extra Discount Applied!
                    </span>

                  </div>

                  <small className="text-muted d-block">

                    Pay full{" "}
                    <b>
                      ₹{amountToPayNow.toLocaleString()}
                    </b>{" "}
                    and save extra{" "}
                    <b>
                      ₹{fullPayDiscount.toLocaleString()}
                    </b>
                    .

                  </small>

                </div>
              )}

              {/* =============================================
                  ONLINE PAYMENT METHODS
                  HIDE FOR PAY LATER
              ============================================= */}

              {paymentMode !== "pay_later" &&
                [
                  {
                    id: "upi",
                    label: "UPI (GPay, PhonePe)",
                    icon: (
                      <FaGooglePay
                        size={26}
                        className="text-dark"
                      />
                    ),
                  },
                  {
                    id: "card",
                    label: "Debit/Credit Card",
                    icon: (
                      <FaCreditCard
                        size={20}
                        className="text-secondary"
                      />
                    ),
                  },
                  {
                    id: "netbanking",
                    label: "Net Banking",
                    icon: (
                      <FaUniversity
                        size={20}
                        className="text-secondary"
                      />
                    ),
                  },
                ].map((m) => (
                  <div
                    key={m.id}
                    onClick={() =>
                      setGatewayMethod(m.id)
                    }
                    className={`d-flex align-items-center p-3 mb-2 rounded-4 cursor-pointer border transition-all ${
                      gatewayMethod === m.id
                        ? "border-primary bg-primary bg-opacity-10"
                        : "border-light"
                    }`}
                  >

                    <div
                      className={`rounded-circle me-3 d-flex align-items-center justify-content-center ${
                        gatewayMethod === m.id
                          ? "bg-primary"
                          : "border border-2"
                      }`}
                      style={{
                        width: 20,
                        height: 20,
                      }}
                    >
                      {gatewayMethod === m.id && (
                        <FaCheckCircle
                          className="text-white"
                          size={12}
                        />
                      )}
                    </div>

                    <div className="me-3">
                      {m.icon}
                    </div>

                    <span
                      className={`flex-grow-1 fw-semibold small ${
                        gatewayMethod === m.id
                          ? "text-primary"
                          : "text-dark"
                      }`}
                    >
                      {m.label}
                    </span>

                  </div>
                ))}

            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          STICKY BOTTOM BAR
      ===================================================== */}

      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #f1f5f9",
          boxShadow:
            "0 -4px 20px rgba(0,0,0,0.08)",
          padding: "16px",
          zIndex: 100,
        }}
      >

        <div
          style={{
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "35px",
            }}
          >

            {/* PAY NOW AREA */}

            <div>

              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {paymentMode === "pay_later"
                  ? "Pay Later"
                  : "Pay Now"}
              </div>

              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#0f172a",
                  lineHeight: "1.1",
                }}
              >
                ₹{amountToPayNow.toLocaleString()}
              </div>

              {paymentMode ===
                "pay_later" && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#dc3545",
                    fontWeight: "600",
                    marginTop: "2px",
                  }}
                >
                  ₹
                  {finalTotalFare.toLocaleString()}{" "}
                  payable later
                </div>
              )}

              {paymentMode !==
                "pay_later" &&
                amountToPayDriver > 0 && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#ef4444",
                      fontWeight: "600",
                      marginTop: "2px",
                    }}
                  >
                    + ₹
                    {amountToPayDriver.toLocaleString()}{" "}
                    to driver later
                  </div>
                )}

            </div>

            {/* MAIN BUTTON */}

            <button
              type="button"
              onClick={handlePayment}
              disabled={loading}
              style={{
                background: loading
                  ? "#94a3b8"
                  : paymentMode ===
                    "pay_later"
                  ? "linear-gradient(135deg, #f59e0b, #fbbf24)"
                  : "linear-gradient(135deg, #f97316, #fb923c)",

                color: "#fff",

                border: "none",

                borderRadius: "16px",

                padding: "16px 28px",

                fontWeight: "800",

                fontSize: "15px",

                cursor: loading
                  ? "not-allowed"
                  : "pointer",

                fontFamily: "inherit",

                minWidth: "160px",

                boxShadow: loading
                  ? "none"
                  : "0 4px 16px rgba(249,115,22,0.4)",

                transition: "all 0.2s",
              }}
            >
              {loading
                ? "Processing…"
                : paymentMode ===
                  "pay_later"
                ? "Confirm Booking"
                : `Pay ₹${amountToPayNow.toLocaleString()}`}
            </button>

          </div>

          {/* SECURE TEXT */}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              fontSize: "11px",
              color: "#94a3b8",
            }}
          >

            <FaShieldAlt
              style={{
                color: "#22c55e",
              }}
            />

            <span>
              {paymentMode === "pay_later"
                ? "Booking Secure · Payment Later"
                : "100% Safe & Secure · Powered by PayU"}
            </span>

          </div>

        </div>
      </div>

      {/* =====================================================
          STYLES
      ===================================================== */}

      <style>{`

        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');

        .android-alert-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100%;
          background: rgba(0, 0, 0, 0.4);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }

        .android-alert-card {
          background: white;
          border-radius: 16px;
          width: 90%;
          max-width: 350px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }

        .animate-pop-in {
          animation: popIn 0.3s cubic-bezier(
            0.175,
            0.885,
            0.32,
            1.275
          ) forwards;
        }

        @keyframes popIn {
          from {
            transform: scale(0.85);
            opacity: 0;
          }

          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        * {
          -webkit-tap-highlight-color: transparent;
        }

        input::placeholder,
        textarea::placeholder {
          color: #cbd5e1;
        }

        .cursor-pointer {
          cursor: pointer;
        }

        .transition-all {
          transition: all 0.2s ease;
        }

        .hover-shadow:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

      `}</style>

    </div>
  );
}
